// src/pages/ResearchChatPage.js
//
// Research Chat — the second chat surface, reached by URL (/research_chat) only.
// It is deliberately absent from the modules catalog, so it never appears in the
// sidebar; access still requires the Aegis Chat module grant.
//
// The chat itself IS GeneralChatPage, rendered with the Research Chat API bundle.
// Sharing the component is the point: any difference visible between the two
// surfaces comes from the retrieval path behind them (self-hosted SearXNG plus
// page fetch and bge-m3 reranking, versus the Brave API), not from two copies of
// a UI drifting apart.
//
// The panel above the chat is this surface's other half: the search audit. Every
// query the turn proposed is listed with whether it was allowed off the server,
// so the privacy claim can be inspected rather than asserted.

import React, { useCallback, useEffect, useState } from "react";
import { FiShield, FiRefreshCw, FiChevronDown, FiChevronUp } from "react-icons/fi";

import GeneralChatPage from "./GeneralChatPage";
import { RESEARCH_CHAT_API, listSearchEvents } from "../api/researchChatApi";

// Its own session key, so opening Research Chat does not hijack the thread the
// user has open in Aegis Chat (and vice versa).
const CONV_KEY = "research_chat_conversation_id";

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

function SearchAudit() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      setRows(await listSearchEvents({ limit: 50, blockedOnly }));
    } catch (e) {
      setError(e.message || "Could not load search events");
    } finally {
      setBusy(false);
    }
  }, [blockedOnly]);

  // Only fetch while the panel is open; a closed panel should not poll the API
  // on every turn.
  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  return (
    <div className="rc-audit">
      <div className="rc-audit-bar">
        <button
          type="button"
          className="rc-audit-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <FiShield aria-hidden="true" />
          <span>Search audit</span>
          {open ? <FiChevronUp aria-hidden="true" /> : <FiChevronDown aria-hidden="true" />}
        </button>

        {open && (
          <div className="rc-audit-controls">
            <label className="rc-audit-check">
              <input
                type="checkbox"
                checked={blockedOnly}
                onChange={(e) => setBlockedOnly(e.target.checked)}
              />
              Blocked only
            </label>
            <button
              type="button"
              className="rc-audit-refresh"
              onClick={refresh}
              disabled={busy}
              title="Reload"
            >
              <FiRefreshCw aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      {open && (
        <div className="rc-audit-body">
          {error && <div className="rc-audit-error">{error}</div>}
          {!error && rows.length === 0 && !busy && (
            <div className="rc-audit-empty">
              No searches recorded yet on this surface.
            </div>
          )}
          {rows.length > 0 && (
            <div className="rc-audit-scroll">
              <table className="rc-audit-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Query</th>
                    <th>Left the server</th>
                    <th>Results</th>
                    <th>Fetched</th>
                    <th>Passages</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={r.egress ? "" : "rc-audit-blocked"}>
                      <td className="rc-audit-when">{formatWhen(r.occurredAt)}</td>
                      <td className="rc-audit-q" title={r.query}>{r.query}</td>
                      <td>
                        {r.egress ? (
                          <span className="rc-badge rc-badge-out">sent</span>
                        ) : (
                          <span className="rc-badge rc-badge-held" title={
                            (r.entities || []).length
                              ? `Blocked on: ${r.entities.join(", ")}`
                              : "Blocked"
                          }>
                            blocked{(r.entities || []).length ? ` — ${r.entities.join(", ")}` : ""}
                          </span>
                        )}
                      </td>
                      <td>{r.egress ? r.resultCount : "—"}</td>
                      <td>{r.egress ? r.fetchedCount : "—"}</td>
                      <td>{r.egress ? r.rankedCount : "—"}</td>
                      <td>{r.latencyMs != null ? `${r.latencyMs} ms` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="rc-audit-foot">
            Records are append-only; the database rejects updates and deletes.
            Shown here: your own searches, 50 most recent.
          </div>
        </div>
      )}
    </div>
  );
}

export default function ResearchChatPage() {
  return (
    <div className="rc-page">
      <div className="rc-banner">
        <strong>Research Chat</strong>
        <span>
          Self-hosted search (SearXNG) with page retrieval and bge-m3 ranking. No
          search vendor API key. Evaluation surface — Aegis Chat is unchanged.
        </span>
      </div>
      <SearchAudit />
      <GeneralChatPage
        api={RESEARCH_CHAT_API}
        convKey={CONV_KEY}
        attachmentsEnabled={false}
      />
    </div>
  );
}
